/* eslint-disable react/no-array-index-key */
import React, { useMemo } from 'react';
import { describe, useGetGlobalAttributes, useGetXstyledProps, useOwnerProps } from '@elliemae/ds-props-helpers';
import { convertPropToCssClassName } from '@elliemae/ds-classnames';
import type { SvgIconT } from './react-desc-prop-types.js';
import { propTypes } from './react-desc-prop-types.js';
import { StyledSvg, StyledWrapper } from './styled.js';

const renderPaths = (paths: string[]) => paths.map((p, i) => <path key={i} d={p} />);

const SvgIcon: React.ComponentType<SvgIconT.Props> = (props) => {
  const { color, size, innerRef, containerProps, paths, fill, viewBox, children, component } = props;

  const { className, width, height, style, title, ...restGlobals } = useGetGlobalAttributes(props);
  const { getOwnerProps, getOwnerPropsArguments } = useOwnerProps(props);

  const defaultWidth = useMemo(() => {
    if (String(width).includes('rem')) return width;
    if (String(height).includes('rem')) return height;
    if (width) return parseInt(width.toString(), 10);
    if (height) return parseInt(height.toString(), 10);

    return 16;
  }, [width, height]);

  const defaultHeight = useMemo(() => {
    if (String(height).includes('rem')) return height;
    if (String(width).includes('rem')) return width;
    if (height) return parseInt(height.toString(), 10);
    if (width) return parseInt(width.toString(), 10);
    return 16;
  }, [width, height]);

  const xstyledProps = useGetXstyledProps(props);

  const { cssClassName } = convertPropToCssClassName('icon', className, {
    size,
    color,
  });

  const finalColor = useMemo(() => {
    if (fill) return fill;
    if (!color) return undefined;
    if (!Array.isArray(color)) return color;
    if (color[0] === 'brand-primary') return `brand-${color[1]}`;
    if (color[0] === 'neutral' && color[1].toString() === '0') return `neutral-000`;
    if (color[0] === 'neutral' && color[1].toString() === '900') return `neutral-800`;
    return `${color[0]}-${color[1]}`;
  }, [color, fill]);

  return (
    <StyledWrapper
      as={component}
      innerRef={innerRef}
      className={cssClassName}
      aria-hidden={title === undefined}
      title={title}
      {...restGlobals}
      {...containerProps}
      {...xstyledProps}
      style={style}
      height={!size ? defaultHeight : undefined}
      width={!size ? defaultWidth : undefined}
      size={size}
      svgColor={finalColor}
      getOwnerProps={getOwnerProps}
      getOwnerPropsArguments={getOwnerPropsArguments}
    >
      <StyledSvg
        size={size}
        viewBox={viewBox}
        width={!size ? defaultWidth : undefined}
        height={!size ? defaultHeight : undefined}
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {paths ? renderPaths(paths) : children}
        {title ? <title>{title}</title> : null}
      </StyledSvg>
    </StyledWrapper>
  );
};

SvgIcon.displayName = 'SvgIcon';
const IconWithSchema = describe(SvgIcon);
IconWithSchema.propTypes = propTypes;

export { IconWithSchema, SvgIcon };
export default SvgIcon;
